import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Inner-page hero. Clears the fixed nav, includes split-headline + reveals.
 *
 * <PageHero
 *   variant="light" | "dark" | "image"      (default "light")
 *   align="left" | "center"                   (default "left")
 *   eyebrow="Case studies"                     (string or node)
 *   title={<>Automation that <em>ships.</em></>}
 *   lead="One or two sentences."
 *   crumbs={[{ to: '/', label: 'Home' }, { label: 'Case studies' }]}
 *   image={{ src: '/images/x.jpg', webp: '/images/x.webp', alt: '', position: '60% 50%' }}  (variant="image")
 *   actions={<><a className="btn btn-primary btn-lg" …/></>}
 * >
 *   optional extra content under the actions (stats row, badges …)
 * </PageHero>
 *
 * dark/image variants set [data-nav-over] so the nav starts transparent.
 */
export default function PageHero({
  variant = 'light',
  align = 'left',
  eyebrow,
  title,
  lead,
  crumbs,
  image,
  actions,
  id,
  className = '',
  children,
}) {
  const dark = variant === 'dark' || variant === 'image';
  const classes = [
    'page-hero',
    `page-hero--${variant}`,
    dark ? 'theme-dark grain' : '',
    align === 'center' ? 'page-hero--center' : '',
    className,
  ].filter(Boolean).join(' ');
  const titleId = id ? `${id}-title` : undefined;

  return (
    <section className={classes} id={id} aria-labelledby={titleId} {...(dark ? { 'data-nav-over': '' } : {})}>
      {variant === 'image' && image && (
        <div className="page-hero__media" aria-hidden={image.alt ? undefined : 'true'}>
          <div data-parallax="-0.12">
            <picture>
              {image.webp && <source srcSet={image.webp} type="image/webp" />}
              <img
                src={image.src}
                alt={image.alt || ''}
                fetchpriority="high"
                decoding="async"
                style={image.position ? { objectPosition: image.position } : undefined}
              />
            </picture>
          </div>
        </div>
      )}
      {variant === 'image' && <div className="page-hero__scrim" aria-hidden="true" />}

      <div className="container page-hero__inner">
        {crumbs && crumbs.length > 0 && (
          <nav aria-label="Breadcrumb" data-reveal>
            <ol className="crumbs">
              {crumbs.map((c, i) => (
                <li key={i}>
                  {c.to && i < crumbs.length - 1
                    ? <Link to={c.to}>{c.label}</Link>
                    : <span aria-current={i === crumbs.length - 1 ? 'page' : undefined}>{c.label}</span>}
                </li>
              ))}
            </ol>
          </nav>
        )}
        {eyebrow && <p className="eyebrow" data-reveal>{eyebrow}</p>}
        <h1 className="page-hero__title" id={titleId} data-split>{title}</h1>
        {lead && <p className="page-hero__lead" data-reveal data-delay="0.2">{lead}</p>}
        {actions && <div className="page-hero__actions" data-reveal data-delay="0.3">{actions}</div>}
        {children && <div className="page-hero__aside" data-reveal data-delay="0.4">{children}</div>}
      </div>
    </section>
  );
}
